import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Copy, Check, CircleAlert, Trash2, ShieldCheck } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { data as fData, plural } from "../lib/formato.js";
import { usePode } from "../shell/Sessao.js";
import { iniciaisDe } from "../shell/MenuUsuario.js";

const PAPEL: Record<string, string> = {
  admin: "Administrador", dp: "Departamento pessoal", rh: "Recursos humanos",
  gestor: "Gestor", colaborador: "Colaborador", contador: "Contador",
  auditor: "Auditor", sst: "Segurança do trabalho",
  medico: "Medicina do trabalho", recrutador: "Recrutamento",
};

export function Equipe() {
  const [convidando, setConvidando] = useState(false);
  const [linkNovo, setLinkNovo] = useState<{ token: string; nome: string } | null>(null);
  const podeConvidar = usePode("usuario.convidar");
  const qc = useQueryClient();

  const membros = useQuery({
    queryKey: ["equipe", "membros"],
    queryFn: () => trpc.equipe.membros.query(),
  });
  const convites = useQuery({
    queryKey: ["equipe", "convites"],
    queryFn: () => trpc.equipe.convites.query(),
  });

  const revogar = useMutation({
    mutationFn: (id: string) => trpc.equipe.revogarConvite.mutate({ id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["equipe"] }),
  });

  return (
    <Pagina
      secao="Configurações" titulo="Quem tem acesso"
      descricao="O papel define o que a pessoa vê e o que ela pode fazer. Convite vence e não vira acesso sozinho."
      acoes={podeConvidar ? (
        <button className="botao" data-variante="primario" onClick={() => setConvidando(true)}>
          <UserPlus aria-hidden size={15} /> Convidar
        </button>
      ) : undefined}
    >
      {membros.isPending && <Carregando linhas={4} />}
      {membros.isError && <Falha erro={membros.error} tentarDeNovo={() => void membros.refetch()} />}

      {linkNovo && (
        <Cartao titulo="Convite criado"
                subtitulo="Este link aparece uma vez só. Copie agora.">
          <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            Entregue para {linkNovo.nome} por um canal que você confia. O sistema
            guarda só o hash do token, então não há como recuperar o link depois.
          </p>
          <LinkConvite token={linkNovo.token} />
          <button className="botao mt-3" data-variante="silencioso"
                  onClick={() => setLinkNovo(null)}>Já copiei</button>
        </Cartao>
      )}

      {convites.isSuccess && convites.data.length > 0 && (
        <Cartao titulo="Convites em aberto"
                subtitulo={`${plural(convites.data.length, "convite", "convites")} aguardando aceite`}>
          <ul className="eop-equipe">
            {convites.data.map((c) => (
              <li key={c.id} data-expirado={c.estado === "expirado" || undefined}>
                <span aria-hidden className="eop-avatar">{iniciaisDe(c.nome)}</span>
                <span className="min-w-0 flex-1">
                  <span className="eop-equipe-nome">{c.nome}</span>
                  <span className="eop-equipe-sub">
                    CPF final {c.cpfFinal} · {PAPEL[c.papel] ?? c.papel} ·{" "}
                    {c.estado === "expirado"
                      ? `venceu em ${fData(c.expiraEm.slice(0, 10))}`
                      : `vence em ${fData(c.expiraEm.slice(0, 10))}`}
                  </span>
                </span>
                <span className="pilula">{c.estado}</span>
                {podeConvidar && (
                  <button className="botao-icone" aria-label={`Revogar convite de ${c.nome}`}
                          onClick={() => revogar.mutate(c.id)}>
                    <Trash2 aria-hidden size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {membros.isSuccess && (
        <Cartao titulo="Com acesso"
                subtitulo={`${plural(membros.data.length, "pessoa", "pessoas")} nesta empresa`}>
          {membros.data.length === 0 ? (
            <EmptyState titulo="Ninguém além de você"
                        descricao="Convide o time de DP para que a operação não dependa de uma pessoa só." />
          ) : (
            <ul className="eop-equipe">
              {membros.data.map((m) => (
                <li key={m.usuarioId} data-inativo={m.estado !== "ativo" || undefined}>
                  <span aria-hidden className="eop-avatar">{iniciaisDe(m.nome)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="eop-equipe-nome">{m.nome}</span>
                    <span className="eop-equipe-sub">
                      CPF final {m.cpfFinal}
                      {m.ultimoLoginEm
                        ? ` · último acesso em ${fData(m.ultimoLoginEm.slice(0, 10))}`
                        : " · nunca entrou"}
                    </span>
                  </span>
                  <span className="pilula">
                    {m.estado === "ativo" && <span aria-hidden className="sinal sinal-bom" />}
                    {PAPEL[m.papel] ?? m.papel}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      )}

      {convidando && (
        <FormConvite
          aoFechar={() => setConvidando(false)}
          aoCriar={(token, nome) => {
            setConvidando(false); setLinkNovo({ token, nome });
            void qc.invalidateQueries({ queryKey: ["equipe"] });
          }}
        />
      )}
    </Pagina>
  );
}

function LinkConvite({ token }: { token: string }) {
  const [copiado, setCopiado] = useState(false);
  const url = `${window.location.origin}/convite/${token}`;

  return (
    <div className="eop-link-convite">
      <code className="min-w-0 flex-1 truncate">{url}</code>
      <button className="botao h-8 px-2.5 text-xs"
              onClick={() => {
                void navigator.clipboard.writeText(url);
                setCopiado(true);
                setTimeout(() => setCopiado(false), 2000);
              }}>
        {copiado ? <Check aria-hidden size={13} /> : <Copy aria-hidden size={13} />}
        {copiado ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
}

function FormConvite(
  { aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: (token: string, nome: string) => void },
) {
  const [f, setF] = useState({ cpf: "", nome: "", papel: "dp", diasDeValidade: 7 });
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => trpc.equipe.convidar.mutate({
      cpf: f.cpf, nome: f.nome.trim(), papel: f.papel as "dp",
      diasDeValidade: f.diasDeValidade, vinculoId: null,
    }),
    onSuccess: (r) => aoCriar(r.token, f.nome.trim()),
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label="Convidar"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "28rem" }}>
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Convidar para o sistema</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            O login é por CPF. O convite gera um link que aparece uma vez e vence
            no prazo que você definir.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); criar.mutate(); }}>
          <div className="grid gap-3.5 sm:grid-cols-2">
            <label className="block">
              <span className="eop-rotulo">CPF</span>
              <input className="campo mt-1.5 num" required inputMode="numeric" value={f.cpf}
                     onChange={(e) => setF((v) => ({ ...v, cpf: e.target.value }))} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Vence em</span>
              <select className="campo mt-1.5" value={String(f.diasDeValidade)}
                      onChange={(e) => setF((v) => ({ ...v, diasDeValidade: Number(e.target.value) }))}>
                <option value="1">1 dia</option>
                <option value="3">3 dias</option>
                <option value="7">7 dias</option>
                <option value="15">15 dias</option>
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Nome</span>
              <input className="campo mt-1.5" required value={f.nome}
                     onChange={(e) => setF((v) => ({ ...v, nome: e.target.value }))} />
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Papel</span>
              <select className="campo mt-1.5" value={f.papel}
                      onChange={(e) => setF((v) => ({ ...v, papel: e.target.value }))}>
                {Object.entries(PAPEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <span className="carimbo mt-1 block">
                {/* O papel médico é o único que enxerga CID, e o desenho é
                    esse: o gestor vê a ausência e não vê o diagnóstico. */}
                {f.papel === "medico"
                  ? "único papel que enxerga CID e laudo"
                  : f.papel === "auditor"
                    ? "só leitura, e toda leitura fica registrada"
                    : "o papel define o que a pessoa vê e o que pode fazer"}
              </span>
            </label>
          </div>
          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario" disabled={criar.isPending}>
              <ShieldCheck aria-hidden size={15} />
              {criar.isPending ? "Criando" : "Criar convite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
